import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, DollarSign, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatePlans = () => {
  const { selectedProperty } = useOutletContext();
  const [ratePlans, setRatePlans] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    unit_type_id: '',
    base_rate: '',
    hourly_rate: '',
    daily_rate: '',
    weekly_rate: '',
    is_refundable: true,
    requires_advance_purchase: '',
    min_los: 1,
    max_los: null,
    rules: []
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [ratePlansRes, unitTypesRes] = await Promise.all([
        axios.get(`${API}/rate-plans/property/${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/properties/${selectedProperty.id}/unit-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setRatePlans(ratePlansRes.data);
      setUnitTypes(unitTypesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRatePlan = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      const submitData = {
        ...formData,
        base_rate: parseFloat(formData.base_rate) || 0,
        hourly_rate: formData.hourly_rate !== '' ? parseFloat(formData.hourly_rate) : null,
        daily_rate: formData.daily_rate !== '' ? parseFloat(formData.daily_rate) : null,
        weekly_rate: formData.weekly_rate !== '' ? parseFloat(formData.weekly_rate) : null,
        min_los: parseInt(formData.min_los) || 1,
        requires_advance_purchase: parseInt(formData.requires_advance_purchase) || 0,
      };
      await axios.post(
        `${API}/rate-plans`,
        submitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rate plan created successfully');
      setDialogOpen(false);
      setFormData({
        name: '',
        unit_type_id: '',
        base_rate: '',
        hourly_rate: '',
        daily_rate: '',
        weekly_rate: '',
        is_refundable: true,
        requires_advance_purchase: '',
        min_los: 1,
        max_los: null,
        rules: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rate plan');
    }
  };

  const handleDeleteRatePlan = async (ratePlanId) => {
    if (!confirm('Are you sure you want to delete this rate plan?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${API}/rate-plans/${ratePlanId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rate plan deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rate plan');
    }
  };

  const getUnitTypeName = (unitTypeId) => {
    const ut = unitTypes.find(u => u.id === unitTypeId);
    return ut?.name || 'Unknown';
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="rate-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Rate Plans & Pricing</h1>
          <p className="text-slate-600 mt-1">Manage pricing strategies and rate plans</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-rate-plan-button">
              <Plus size={18} />
              New Rate Plan
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Rate Plan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRatePlan} className="space-y-4" data-testid="rate-plan-form">
              <div>
                <Label>Plan Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Best Available Rate"
                  required
                  data-testid="plan-name-input"
                />
              </div>

              <div>
                <Label>Room Type *</Label>
                <Select value={formData.unit_type_id} onValueChange={(val) => setFormData({...formData, unit_type_id: val})}>
                  <SelectTrigger data-testid="unit-type-select">
                    <SelectValue placeholder="Select room type" />
                  </SelectTrigger>
                  <SelectContent>
                    {unitTypes.map(ut => (
                      <SelectItem key={ut.id} value={ut.id}>
                        {ut.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-semibold">Rates</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Nightly Rate *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.base_rate}
                      onChange={(e) => setFormData({...formData, base_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      required
                      placeholder="89.00"
                      data-testid="base-rate-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Hourly Rate</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({...formData, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      placeholder="15.00"
                      data-testid="hourly-rate-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Daily Rate (day use)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.daily_rate}
                      onChange={(e) => setFormData({...formData, daily_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      placeholder="59.00"
                      data-testid="daily-rate-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Weekly Rate</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.weekly_rate}
                      onChange={(e) => setFormData({...formData, weekly_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      placeholder="499.00"
                      data-testid="weekly-rate-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Minimum Stay (nights)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.min_los}
                    onChange={(e) => setFormData({...formData, min_los: e.target.value === '' ? '' : parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Advance Purchase (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.requires_advance_purchase}
                    onChange={(e) => setFormData({...formData, requires_advance_purchase: e.target.value === '' ? '' : parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="refundable"
                  checked={formData.is_refundable}
                  onChange={(e) => setFormData({...formData, is_refundable: e.target.checked})}
                />
                <Label htmlFor="refundable" className="cursor-pointer">Refundable</Label>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-rate-plan-button">
                Create Rate Plan
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {ratePlans.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No rate plans yet. Create your first rate plan to start pricing rooms.</p>
            <Button onClick={() => setDialogOpen(true)} data-testid="empty-create-button">
              <Plus size={18} className="mr-2" />
              Create First Rate Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rate-plans-list">
          {ratePlans.map((plan) => (
            <Card key={plan.id} className="hover:shadow-lg transition-shadow" data-testid={`rate-plan-${plan.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{plan.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRatePlan(plan.id)}
                    data-testid={`delete-plan-${plan.id}`}
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-slate-600">Room Type</p>
                  <p className="font-medium text-slate-900">{getUnitTypeName(plan.unit_type_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Rates</p>
                  <p className="text-2xl font-bold text-cyan-600">${plan.base_rate}<span className="text-sm font-normal text-slate-500">/night</span></p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    {plan.hourly_rate != null && <span className="text-sm text-slate-600">${plan.hourly_rate}/hr</span>}
                    {plan.daily_rate != null && <span className="text-sm text-slate-600">${plan.daily_rate}/day</span>}
                    {plan.weekly_rate != null && <span className="text-sm text-slate-600">${plan.weekly_rate}/wk</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {plan.is_refundable && (
                    <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                      Refundable
                    </span>
                  )}
                  {!plan.is_refundable && (
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                      Non-Refundable
                    </span>
                  )}
                  {plan.min_los > 1 && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      Min {plan.min_los} nights
                    </span>
                  )}
                  {plan.requires_advance_purchase > 0 && (
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                      {plan.requires_advance_purchase} days advance
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RatePlans;
